import { useCallback, useState, useEffect, useContext } from "react"
import { useDispatch } from "react-redux"
import { DataAccess } from "../Models/Client/DataAccess"
import { MainThunkDispatch } from "../Reducers"
import { useAppState } from "./useReduxState"
import { getSignedUrl } from "../Actions/API/Data/DataActions"
import { hasValue } from "../Models/Client/FetchState"
import {
    extractDataUrlPath,
    getMillisecondsUntilExpire
} from "../Models/API/DataModel"
import { DataAccessContext } from "../Context/DataAccessContext"

interface SigningRequest {
    path: string
    resolver: (signedUrl: string) => void
    dataAccess: DataAccess
}

export function useUrlSigner(
    dataAccess: DataAccess
): (url: string) => Promise<string> {
    const dispatch = useDispatch<MainThunkDispatch>()
    const signedUrls = useAppState(state => state.api.data.signedUrls)
    const [requests, setRequests] = useState<SigningRequest[]>([])

    useEffect(() => {
        const removeIndices: number[] = []

        requests.forEach(({ path, resolver }, index) => {
            const signedUrl = signedUrls[path]

            if (
                hasValue(signedUrl) &&
                getMillisecondsUntilExpire(signedUrl.value) > 10 * 1000 // time length is enough to fetch new signed url from backend and also not too long to prevent fetch frequently
            ) {
                resolver(signedUrl.value)
                removeIndices.push(index)
            }
        })

        if (removeIndices.length > 0) {
            setRequests(current =>
                current.filter((_, index) => !removeIndices.includes(index))
            )
        }
    }, [signedUrls, requests])

    useEffect(() => {
        for (const { path, dataAccess } of requests) {
            dispatch(getSignedUrl(path, dataAccess))
        }
    }, [dispatch, requests])

    return useCallback(
        url =>
            new Promise<string>(resolver => {
                const path = extractDataUrlPath(url)

                if (path === undefined) {
                    resolver(url)
                    return
                }

                setRequests(current =>
                    current.concat([{ path, resolver, dataAccess }])
                )
            }),
        // only the content is required to be matched
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [JSON.stringify(dataAccess)]
    )
}

export const useUrlSignerFromContext = () => {
    const dataAccess = useContext(DataAccessContext)
    return useUrlSigner(dataAccess)
}
